import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, Divider, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import * as React from 'react';
import MuiDialogHeader from '../Shared/MuiDialogHeader';
import { useTranslation } from 'react-i18next';
import { DateOnlyDto, GetTenantPermissionSettingsResponse, ModuleType, SaveTenantHiddenPermissionsCommand, SaveTenantPermissionSettingsCommand, TenantPermissionVisibilityDTO, UsersGateApiClient } from '../Api/ApiServerHooks';
import { useGateApi } from '../Api/useGateApi';
import GiffLoader from '../Shared/GiffLoader';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
// Import locales you need
import { isPricingDatabasePermission } from '../Permissions/pricingDatabasePermissions';
import { getPermissionModuleType } from '../Permissions/sumecPermissions';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

const dateOnlyToDayjs = (date: DateOnlyDto | undefined): Dayjs | null => {
    if (!date?.year) return null;
    return dayjs().year(date.year).month(date.month! - 1).date(date.day!);
};

const dayjsToDateOnly = (value: Dayjs | null): DateOnlyDto | undefined => {
    if (!value) return undefined;
    return new DateOnlyDto({ year: value.year(), month: value.month() + 1, day: value.date() });
};

interface ModulePermissionRowProps {
    moduleKey: string;
    label: string;
    hasModule: boolean;
    expiryDate: DateOnlyDto | undefined;
    disabled: boolean;
    onCheckChange: (checked: boolean) => void;
    onDateChange: (date: DateOnlyDto | undefined) => void;
    t: (key: string) => string;
}

const ModulePermissionRow = React.memo(({ 
    moduleKey, 
    label, 
    hasModule, 
    expiryDate, 
    disabled, 
    onCheckChange, 
    onDateChange,
    t 
}: ModulePermissionRowProps) => (
    <Box key={moduleKey} display={'flex'} justifyContent={'space-between'} sx={{ width: '100%', p:1 }}>
        <FormControlLabel
            sx={{ marginLeft: '16px' }}
            control={
                <Checkbox 
                    disabled={disabled} 
                    checked={hasModule} 
                    onChange={(_, checked) => onCheckChange(checked)} 
                />
            }
            label={label}
        />
        <DatePicker 
                disabled={disabled || !hasModule} 
                label={t('Licensing.ExpiryDate')} 
                value={dateOnlyToDayjs(expiryDate)} 
                onChange={(newValue) => onDateChange(dayjsToDateOnly(newValue))}
                slotProps={{
                    actionBar: {
                        actions: ['clear'],
                    },
                    textField: {
                        size: 'small',
                        // This helps with focus issues in Edge
                        inputProps: {
                            autoComplete: 'off',
                        },
                    },
                }}
            />
    </Box>
));

export interface IPermissionSettingsDialogProps {
    isOpen: boolean;
    onClose: (refresh: boolean) => void;
    tenantId: number;
}

export default function PermissionSettingsDialog(props: IPermissionSettingsDialogProps) {
    const { isOpen, onClose, tenantId } = props;
    const { t, i18n } = useTranslation();
    const { authorizedApi } = useGateApi(UsersGateApiClient);
    
    // Get current locale from i18n
    const currentLocale = i18n.language?.split('-')[0] || 'en';
    const [data, setData] = React.useState<GetTenantPermissionSettingsResponse>();
    const [visibility, setVisibility] = React.useState<TenantPermissionVisibilityDTO[]>();
    
    const load = React.useCallback(async () => {
        console.log('Loading tenant permissions for tenantId:', tenantId);
        if (tenantId > 0) {
            console.log('Fetching tenant permissions from API');
            const response = await authorizedApi?.getTenantsPermissions(tenantId);
            console.log('Received tenant permissions:', response);
            if (response)
                setData(response);

            // Which permissions this tenant is allowed to see at all. Separate endpoint because it
            // is a global-admin decision, not part of the module licence.
            const hidden = await authorizedApi?.getTenantHiddenPermissions(tenantId);
            if (hidden?.permissions)
                setVisibility(hidden.permissions);
        }
    }, [authorizedApi, tenantId]);
    
    React.useEffect(() => {
        load();
    }, [load]);

    // Pricing databases are seeded under ModuleType.DataStandards but are a separate feature, so
    // they get their own group here rather than appearing under "Správce datových standardů".
    const pricingDatabaseVisibility = React.useMemo(
        () => (visibility ?? []).filter(p => isPricingDatabasePermission(p.permissionType)),
        [visibility]);

    const moduleVisibility = React.useMemo(
        () => (visibility ?? []).filter(p => !isPricingDatabasePermission(p.permissionType)),
        [visibility]);

    // Only render module headings that actually carry permissions, so the list stays short as the
    // catalogue grows.
    const visibleModuleTypes = React.useMemo(
        () => Array.from(new Set(moduleVisibility.map(p => getPermissionModuleType(p)!))).sort((a, b) => a - b),
        [moduleVisibility]);

    const renderVisibilityCheckbox = React.useCallback((perm: TenantPermissionVisibilityDTO) => (
        <FormControlLabel
            key={perm.id}
            label={t('Permissions.' + (perm.name ?? ''))}
            control={
                <Checkbox
                    checked={perm.visible ?? false}
                    onChange={(_, checked) => setVisibility(prev => prev?.map(p =>
                        p.id === perm.id
                            ? new TenantPermissionVisibilityDTO({ ...p, visible: checked })
                            : p))}
                />
            }
        />
    ), [t]);

    const updateField = React.useCallback(<K extends keyof GetTenantPermissionSettingsResponse>(
        field: K, 
        value: GetTenantPermissionSettingsResponse[K]
    ) => {
        setData(prev => {
            if (!prev) return prev;
            // Create a shallow copy instead of new instance
            return { ...prev, [field]: value } as GetTenantPermissionSettingsResponse;
        });
    }, []);

    return (
        <Dialog onClose={() => onClose(false)} open={isOpen} sx={{ zIndex: 1299 }}>
            <MuiDialogHeader showCloseText title={t('Licensing.LicenseSettings')} onClose={() => onClose(false)} />

            <DialogContent>
                {!data && <GiffLoader />}
                {data && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale}>
                        <Stack spacing={2} sx={{ padding: '16px' }}>
                            <Stack direction='row' spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                                <Typography
                                    fontWeight={data.restrictModules ? 'normal' : 'bold'}
                                    color={data.restrictModules ? 'action' : 'primary'}>{t('Licensing.AllModulesAllowed')}</Typography>
                                <Switch checked={data.restrictModules ?? false} onChange={(_, checked) => {
                                    if (checked)
                                        setData(prev => ({
                                            ...prev,
                                            hasBimValidator: true,
                                            hasBudgeting: true,
                                            hasCurveSolver: true,
                                            hasSlopeGuard: true,
                                            hasEnumerationsAdministration: true,
                                            hasIsoAnalyzer: true,
                                            hasOvCe: true,
                                            hasBimVitrina: true,
                                            restrictModules: checked
                                        } as GetTenantPermissionSettingsResponse));
                                    else setData(prev => ({ ...prev, restrictModules: checked } as GetTenantPermissionSettingsResponse));
                                }} />
                                <Typography
                                    fontWeight={data.restrictModules ? 'bold' : 'normal'}
                                    color={data.restrictModules ? 'primary' : 'action'}>{t('Licensing.TurnOfSomeModules')}</Typography>
                            </Stack>
                            <Stack sx={{ border: 'none', margin: 0, padding: 0 }} gap={1}>
                                <ModulePermissionRow
                                    moduleKey="bimValidator"
                                    label={t('Licensing.BimValidator')}
                                    hasModule={data.hasBimValidator ?? false}
                                    expiryDate={data.bimValidatorExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasBimValidator', checked)}
                                    onDateChange={(date) => updateField('bimValidatorExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="budgeting"
                                    label={t('Licensing.BimBudgeting')}
                                    hasModule={data.hasBudgeting ?? false}
                                    expiryDate={data.budgetingExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasBudgeting', checked)}
                                    onDateChange={(date) => updateField('budgetingExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="curveSolver"
                                    label={t('Licensing.CurveSolver')}
                                    hasModule={data.hasCurveSolver ?? false}
                                    expiryDate={data.curveSolverExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasCurveSolver', checked)}
                                    onDateChange={(date) => updateField('curveSolverExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="slopeGuard"
                                    label={t('ModuleType.SlopeGuard')}
                                    hasModule={data.hasSlopeGuard ?? false}
                                    expiryDate={data.slopeGuardExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasSlopeGuard', checked)}
                                    onDateChange={(date) => updateField('slopeGuardExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="enumerations"
                                    label={t('Licensing.Enums')}
                                    hasModule={data.hasEnumerationsAdministration ?? false}
                                    expiryDate={data.enumerationsAdministrationExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasEnumerationsAdministration', checked)}
                                    onDateChange={(date) => updateField('enumerationsAdministrationExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="isoAnalyzer"
                                    label={t('Licensing.ISOAnalyzer')}
                                    hasModule={data.hasIsoAnalyzer ?? false}
                                    expiryDate={data.isoAnalyzerExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasIsoAnalyzer', checked)}
                                    onDateChange={(date) => updateField('isoAnalyzerExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="ovce"
                                    label={t('Licensing.Ovce')}
                                    hasModule={data.hasOvCe ?? false}
                                    expiryDate={data.ovCeExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasOvCe', checked)}
                                    onDateChange={(date) => updateField('ovCeExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="bimVitrina"
                                    label={t('Licensing.BimVitrina')}
                                    hasModule={data.hasBimVitrina ?? false}
                                    expiryDate={data.bimVitrinaExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasBimVitrina', checked)}
                                    onDateChange={(date) => updateField('bimVitrinaExpiryDate', date)}
                                    t={t}
                                />
                                <ModulePermissionRow
                                    moduleKey="aiAsker"
                                    label={t('ModuleType.AiAsker')}
                                    hasModule={data.hasAiAsker ?? false}
                                    expiryDate={data.aiAskerExpiryDate}
                                    disabled={data.restrictModules !== true}
                                    onCheckChange={(checked) => updateField('hasAiAsker', checked)}
                                    onDateChange={(date) => updateField('aiAskerExpiryDate', date)}
                                    t={t}
                                />
                            </Stack>
                            <Divider />
                            <Box>
                                <Typography variant='h6'>{t('Administration.hiddenPermissions')}</Typography>
                                <Typography variant='body2' color='text.secondary'>
                                    {t('Administration.hiddenPermissionsHint')}
                                </Typography>
                            </Box>
                            {pricingDatabaseVisibility.length > 0 && (
                                <Box sx={{ pl: 2 }}>
                                    <Typography variant='subtitle2'>{t('PermissionGroups.PricingDatabases')}</Typography>
                                    <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column' }}>
                                        {pricingDatabaseVisibility.map(renderVisibilityCheckbox)}
                                    </Box>
                                </Box>
                            )}
                            {visibleModuleTypes.map(moduleType => (
                                <Box key={moduleType} sx={{ pl: 2 }}>
                                    <Typography variant='subtitle2'>{t('ModuleType.' + ModuleType[moduleType])}</Typography>
                                    <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column' }}>
                                        {moduleVisibility
                                            .filter(p => getPermissionModuleType(p) === moduleType)
                                            .map(renderVisibilityCheckbox)}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </LocalizationProvider>
                )}
            </DialogContent>
            <DialogActions>
                <Stack direction='row' spacing={2} sx={{ padding: '8px 16px' }}>
                    <Button variant='outlined' color='primary' onClick={() => { onClose(false) }}>{t('Close')}</Button>
                    <Button disabled={!data} variant='contained' color='primary' onClick={() => {
                        console.log('Saving tenant permissions for tenantId:', tenantId, 'with data:', data);
                        if (data)
                            authorizedApi?.saveTenantPermissions(new SaveTenantPermissionSettingsCommand({
                                tenantId: tenantId,
                                restrictModules: data.restrictModules,
                                hasBimValidator: data.hasBimValidator,
                                hasBudgeting: data.hasBudgeting,
                                hasCurveSolver: data.hasCurveSolver,
                                hasSlopeGuard: data.hasSlopeGuard,
                                hasEnumerationsAdministration: data.hasEnumerationsAdministration,
                                hasIsoAnalyzer: data.hasIsoAnalyzer,
                                hasOvCe: data.hasOvCe,
                                hasBimVitrina: data.hasBimVitrina,
                                hasAiAsker: data.hasAiAsker,

                                bimValidatorExpiryDate: data.bimValidatorExpiryDate ? new DateOnlyDto({ year: data.bimValidatorExpiryDate.year, month: data.bimValidatorExpiryDate.month, day: data.bimValidatorExpiryDate.day }) : undefined,
                                budgetingExpiryDate: data.budgetingExpiryDate ? new DateOnlyDto({ year: data.budgetingExpiryDate.year, month: data.budgetingExpiryDate.month, day: data.budgetingExpiryDate.day }) : undefined,
                                curveSolverExpiryDate: data.curveSolverExpiryDate ? new DateOnlyDto({ year: data.curveSolverExpiryDate.year, month: data.curveSolverExpiryDate.month, day: data.curveSolverExpiryDate.day }) : undefined,
                                slopeGuardExpiryDate: data.slopeGuardExpiryDate ? new DateOnlyDto({ year: data.slopeGuardExpiryDate.year, month: data.slopeGuardExpiryDate.month, day: data.slopeGuardExpiryDate.day }) : undefined,
                                isoAnalyzerExpiryDate: data.isoAnalyzerExpiryDate ? new DateOnlyDto({ year: data.isoAnalyzerExpiryDate.year, month: data.isoAnalyzerExpiryDate.month, day: data.isoAnalyzerExpiryDate.day }) : undefined,
                                enumerationsAdministrationExpiryDate: data.enumerationsAdministrationExpiryDate ? new DateOnlyDto({ year: data.enumerationsAdministrationExpiryDate.year, month: data.enumerationsAdministrationExpiryDate.month, day: data.enumerationsAdministrationExpiryDate.day }) : undefined,
                                ovCeExpiryDate: data.ovCeExpiryDate ? new DateOnlyDto({ year: data.ovCeExpiryDate.year, month: data.ovCeExpiryDate.month, day: data.ovCeExpiryDate.day }) : undefined,
                                bimVitrinaExpiryDate: data.bimVitrinaExpiryDate ? new DateOnlyDto({ year: data.bimVitrinaExpiryDate.year, month: data.bimVitrinaExpiryDate.month, day: data.bimVitrinaExpiryDate.day }) : undefined,
                                aiAskerExpiryDate: data.aiAskerExpiryDate ? new DateOnlyDto({ year: data.aiAskerExpiryDate.year, month: data.aiAskerExpiryDate.month, day: data.aiAskerExpiryDate.day }) : undefined,

                            })).then(async (response) => {
                                if (!response?.success) return;

                                // Visibility is stored separately from the module licence, so it needs its own
                                // call before the dialog reports success.
                                if (visibility)
                                    await authorizedApi?.saveTenantHiddenPermissions(new SaveTenantHiddenPermissionsCommand({
                                        tenantId: tenantId,
                                        permissions: visibility
                                    }));

                                onClose(true);
                            });
                    }}>{t('Save')}</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}